export const SYNC_ENTITIES = Object.freeze({
  workspace: 'workspace',
  currentUser: 'currentUser',
  workspaceUsers: 'workspaceUsers',
  companies: 'companies',
  companyClients: 'companyClients',
  companyManagers: 'companyManagers',
  tasks: 'tasks',
  taskSubtasks: 'taskSubtasks',
  taskAttachments: 'taskAttachments',
  attendanceLogs: 'attendanceLogs',
  userSettings: 'userSettings',
  prayerStatus: 'prayerStatus'
});

export const SYNC_TABLES = Object.freeze({
  workspaces: 'workspaces',
  users: 'users',
  companies: 'companies',
  companyClients: 'company_clients',
  companyManagers: 'company_managers',
  tasks: 'tasks',
  taskSubtasks: 'task_subtasks',
  taskAttachments: 'task_attachments',
  attendanceLogs: 'attendance_logs',
  userSettings: 'user_settings',
  prayerStatus: 'prayer_status'
});

export const SYNC_MAP = Object.freeze({
  [SYNC_ENTITIES.workspace]: {
    table: SYNC_TABLES.workspaces,
    localSource: 'currentWorkspace',
    primaryKey: 'id',
    relationships: [],
    fields: {
      id: 'id',
      name: 'name',
      ownerUserId: 'owner_user_id',
      mode: 'mode',
      createdAt: 'created_at'
    }
  },
  [SYNC_ENTITIES.currentUser]: {
    table: SYNC_TABLES.users,
    localSource: 'currentUser',
    primaryKey: 'id',
    relationships: ['workspace_id -> workspaces.id'],
    fields: {
      id: 'id',
      workspaceId: 'workspace_id',
      email: 'email',
      name: 'display_name',
      title: 'job_title',
      photo: 'photo_url',
      accessRole: 'role'
    }
  },
  [SYNC_ENTITIES.workspaceUsers]: {
    table: SYNC_TABLES.users,
    localSource: 'workspaceUsers',
    primaryKey: 'id',
    relationships: ['workspace_id -> workspaces.id'],
    fields: {
      id: 'id',
      workspaceId: 'workspace_id',
      email: 'email',
      name: 'display_name',
      title: 'job_title',
      photo: 'photo_url',
      accessRole: 'role',
      status: 'status',
      createdAt: 'created_at'
    }
  },
  [SYNC_ENTITIES.companies]: {
    table: SYNC_TABLES.companies,
    localSource: 'companies',
    primaryKey: 'id',
    relationships: ['workspace_id -> workspaces.id'],
    fields: {
      id: 'id',
      workspaceId: 'workspace_id',
      name: 'name',
      logo: 'logo_url',
      theme: 'color_theme',
      systemEnabled: 'system_enabled',
      systemUrl: 'system_url',
      driveEnabled: 'drive_enabled',
      driveUrl: 'drive_url',
      workStart: 'work_start',
      workEnd: 'work_end',
      workDays: 'work_days',
      hourlyRate: 'hourly_rate',
      overtimeMinutes: 'overtime_minutes',
      isClockedIn: 'is_clocked_in',
      clockInTime: 'clock_in_time'
    }
  },
  [SYNC_ENTITIES.companyClients]: {
    table: SYNC_TABLES.companyClients,
    localSource: 'companies.*.clients',
    primaryKey: 'id',
    relationships: ['company_id -> companies.id'],
    fields: {
      id: 'id',
      companyId: 'company_id',
      name: 'name'
    }
  },
  [SYNC_ENTITIES.companyManagers]: {
    table: SYNC_TABLES.companyManagers,
    localSource: 'companies.*.managers',
    primaryKey: 'id',
    relationships: ['company_id -> companies.id'],
    fields: {
      id: 'id',
      companyId: 'company_id',
      name: 'name'
    }
  },
  [SYNC_ENTITIES.tasks]: {
    table: SYNC_TABLES.tasks,
    localSource: 'tasks',
    primaryKey: 'id',
    relationships: ['workspace_id -> workspaces.id', 'company_id -> companies.id'],
    fields: {
      id: 'id',
      workspaceId: 'workspace_id',
      category: 'company_id',
      title: 'title',
      description: 'description',
      client: 'client_name',
      accountManager: 'manager_name',
      designType: 'design_type',
      selectedPlatforms: 'selected_platforms',
      estimatedHours: 'estimated_hours',
      isUrgent: 'is_urgent',
      status: 'status',
      elapsedTime: 'elapsed_time',
      isRunning: 'is_running',
      brief: 'brief',
      tov: 'tov',
      hasBrief: 'has_brief',
      hasTov: 'has_tov',
      createdAt: 'created_at',
      startedAt: 'started_at',
      deadlineDate: 'deadline_date',
      deadlineTime: 'deadline_time',
      deadlineAt: 'deadline_at',
      completedAt: 'completed_at'
    }
  },
  [SYNC_ENTITIES.taskSubtasks]: {
    table: SYNC_TABLES.taskSubtasks,
    localSource: 'tasks.*.subtasks',
    primaryKey: 'id',
    relationships: ['task_id -> tasks.id'],
    fields: {
      id: 'id',
      taskId: 'task_id',
      text: 'text',
      completed: 'completed',
      sortOrder: 'sort_order'
    }
  },
  [SYNC_ENTITIES.taskAttachments]: {
    table: SYNC_TABLES.taskAttachments,
    localSource: 'tasks.*.images',
    primaryKey: 'id',
    relationships: ['task_id -> tasks.id'],
    fields: {
      id: 'id',
      taskId: 'task_id',
      fileUrl: 'file_url',
      fileType: 'file_type'
    }
  },
  [SYNC_ENTITIES.attendanceLogs]: {
    table: SYNC_TABLES.attendanceLogs,
    localSource: 'companies.*.attendanceLogs',
    primaryKey: 'id',
    relationships: ['company_id -> companies.id', 'user_id -> users.id'],
    fields: {
      id: 'id',
      companyId: 'company_id',
      userId: 'user_id',
      clockInAt: 'clock_in_at',
      clockOutAt: 'clock_out_at',
      durationSeconds: 'duration_seconds'
    }
  },
  [SYNC_ENTITIES.userSettings]: {
    table: SYNC_TABLES.userSettings,
    localSource: 'user settings state',
    primaryKey: 'user_id',
    relationships: ['user_id -> users.id'],
    fields: {
      userId: 'user_id',
      lang: 'language',
      theme: 'theme',
      uiDensity: 'ui_density',
      governorate: 'governorate',
      adhanVoice: 'adhan_voice',
      volume: 'sound_volume',
      healthEnabled: 'health_enabled',
      healthInterval: 'health_interval'
    }
  },
  [SYNC_ENTITIES.prayerStatus]: {
    table: SYNC_TABLES.prayerStatus,
    localSource: 'prayerStatus',
    primaryKey: 'id',
    relationships: ['user_id -> users.id'],
    fields: {
      id: 'id',
      userId: 'user_id',
      date: 'date',
      data: 'data'
    }
  }
});

export const getSyncEntityConfig = (entity) => SYNC_MAP[entity] || null;
